import { environment } from "../../config/environment";
import React, { useState, useEffect } from 'react';
import { customFetch } from '../../utils/Auth';
import Sidebar from '../Sidebar';

const RevOp = ({ setAuth, userOrg, setUserOrg }) => {
  const [invoices, setInvoices] = useState([]);
  const [charges, setCharges] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [invoicePayments, setInvoicePayments] = useState([]);
  const [totalInvoices, setTotalInvoices] = useState(0);
  const [totalCharges, setTotalCharges] = useState(0);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalInvoicePayments, setTotalInvoicePayments] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('invoices');
  const [paymentAnalysis, setPaymentAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [brexTransactions, setBrexTransactions] = useState([]);
  const [brexAccount, setBrexAccount] = useState(null);
  const [brexLoading, setBrexLoading] = useState(false);
  const [filters, setFilters] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    limit: 100,
    email: ''
  });

  // Helper functions for localStorage caching
  const getCachedData = (key) => {
    try {
      const cached = localStorage.getItem(`revop_${key}`);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        // Check if data is less than 5 minutes old
        if (Date.now() - timestamp < 5 * 60 * 1000) {
          return data;
        }
      }
    } catch (error) {
      console.warn('Error reading cached data:', error);
    }
    return null;
  };

  const setCachedData = (key, data) => {
    try {
      localStorage.setItem(`revop_${key}`, JSON.stringify({
        data,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.warn('Error caching data:', error);
    }
  };

  const fetchChargesAndCustomers = async (useCache = true) => {
    setLoading(true);
    setError('');
    
    // Check cache first if useCache is true
    if (useCache) {
      const cacheKey = `charges_customers_${filters.month}_${filters.year}_${filters.limit}`;
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        setInvoices(cachedData.invoices);
        setCharges(cachedData.charges);
        setCustomers(cachedData.customers || []);
        setInvoicePayments(cachedData.invoice_payments);
        setTotalInvoices(cachedData.total_invoices);
        setTotalCharges(cachedData.total_charges);
        setTotalCustomers(cachedData.total_customers || 0);
        setTotalInvoicePayments(cachedData.total_invoice_payments);
        setLoading(false);
        return;
      }
    }
    
    try {
      const response = await customFetch(
        `${environment.apiUrl}/revop/charges-and-customers?month=${filters.month}&year=${filters.year}&limit=${filters.limit}`
      );
      
      // Check if response is JSON
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Server returned HTML instead of JSON. Status: ${response.status}`);
      }
      
      // Parse the JSON response
      const data = await response.json();
      
      if (data.invoices && data.charges && data.invoice_payments) {
        setInvoices(data.invoices);
        setCharges(data.charges);
        setCustomers(data.customers || []);
        setInvoicePayments(data.invoice_payments);
        setTotalInvoices(data.total_invoices);
        setTotalCharges(data.total_charges);
        setTotalCustomers(data.total_customers || 0);
        setTotalInvoicePayments(data.total_invoice_payments);
        
        // Cache the data
        const cacheKey = `charges_customers_${filters.month}_${filters.year}_${filters.limit}`;
        setCachedData(cacheKey, data);
        
        if (data.invoices.length === 0 && data.charges.length === 0 && data.invoice_payments.length === 0) {
          setError('No data found for the selected period');
        }
      } else {
        setError('No data found - API returned empty response');
      }
    } catch (err) {
      const errorMessage = err.details || err.message || 'Failed to fetch charges and customers';
      setError(`Error: ${errorMessage}`);
    } finally {
      setLoading(false);
    }
  };


  const fetchPaymentAnalysis = async (useCache = true) => {
    setAnalysisLoading(true);
    setError('');
    
    // Check cache first if useCache is true
    if (useCache) {
      const cacheKey = `payment_analysis_${filters.month}_${filters.year}_${filters.limit}`;
      const cachedData = getCachedData(cacheKey);
      if (cachedData) {
        setPaymentAnalysis(cachedData);
        setAnalysisLoading(false);
        return;
      }
    }
    
    try {
      const response = await customFetch(
        `${environment.apiUrl}/revop/payment-analysis?month=${filters.month}&year=${filters.year}&limit=${filters.limit}`
      );
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Server returned HTML instead of JSON. Status: ${response.status}`);
      }
      
      const data = await response.json();
      setPaymentAnalysis(data);
      
      // Cache the data
      const cacheKey = `payment_analysis_${filters.month}_${filters.year}_${filters.limit}`;
      setCachedData(cacheKey, data);
      
    } catch (err) {
      const errorMessage = err.details || err.message || 'Failed to fetch payment analysis';
      setError(`Error: ${errorMessage}`);
    } finally {
      setAnalysisLoading(false);
    }
  };

  const fetchBrexTransactions = async () => {
    setBrexLoading(true);
    setError('');
    
    try {
      // Build URL with query parameters
      const url = new URL(`${environment.apiUrl}/revop/brex-transactions`);
      
      // Convert month/year to start date for Brex API
      const startDate = new Date(filters.year, filters.month - 1, 1);
      const endDate = new Date(filters.year, filters.month, 0, 23, 59, 59, 999);
      
      url.searchParams.set('start_date', startDate.toISOString());
      url.searchParams.set('end_date', endDate.toISOString());
      url.searchParams.set('limit', filters.limit);
      
      const response = await customFetch(url.toString());
      
      const contentType = response.headers.get('content-type');
      if (!contentType || !contentType.includes('application/json')) {
        const text = await response.text();
        throw new Error(`Server returned HTML instead of JSON. Status: ${response.status}`);
      }
      
      const data = await response.json();
      
      // Extract transactions from response
      const transactions = data.transactions?.items || [];
      
      setBrexTransactions(transactions);
      setBrexAccount(data.cash_account);
      
    } catch (err) {
      const errorMessage = err.details || err.message || 'Failed to fetch Brex transactions';
      setError(`Error: ${errorMessage}`);
    } finally {
      setBrexLoading(false);
    }
  };

  // Pre-load function that can be called externally (e.g., on app login)
  const preloadRevOpData = async () => {
    const currentMonth = new Date().getMonth() + 1;
    const currentYear = new Date().getFullYear();
    
    // Check if we already have fresh data for current month
    const cacheKey = `charges_customers_${currentMonth}_${currentYear}_100`;
    const cachedData = getCachedData(cacheKey);
    
    if (!cachedData) {
      try {
        // Temporarily update filters for pre-loading
        const originalFilters = { ...filters };
        setFilters({
          month: currentMonth,
          year: currentYear,
          limit: 100,
          email: ''
        });
        
        // Fetch data in background
        await Promise.all([
          fetchChargesAndCustomers(false), // Don't use cache for pre-loading
          fetchPaymentAnalysis(false)
        ]);
        
        // Restore original filters
        setFilters(originalFilters);
      } catch (error) {
        // Silent fail for pre-loading
      }
    }
  };

  // Remove auto-loading - data will only be loaded when user clicks "Load Data" button

  // Expose preload function globally for external use (e.g., on app login)
  useEffect(() => {
    window.preloadRevOpData = preloadRevOpData;
    return () => {
      delete window.preloadRevOpData;
    };
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatAmount = (amount) => {
    return `$${(amount / 100).toFixed(2)}`;
  };

  const formatDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const formatCustomerDate = (timestamp) => {
    return new Date(timestamp * 1000).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'succeeded': return 'text-green-600 bg-green-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'pending': return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Filter invoices and charges by customer email (client-side filtering)
  const filteredInvoices = invoices.filter(invoice => {
    if (!filters.email) return true; // Show all invoices if no email filter
    const customerEmail = invoice.customer_email ? invoice.customer_email.toLowerCase() : '';
    const searchEmail = filters.email.toLowerCase();
    return customerEmail.includes(searchEmail);
  });

  const filteredCharges = charges.filter(charge => {
    if (!filters.email) return true; // Show all charges if no email filter
    const customerEmail = charge.customer_info?.email ? charge.customer_info.email.toLowerCase() : '';
    const searchEmail = filters.email.toLowerCase();
    return customerEmail.includes(searchEmail);
  });

  return (
    <div className="flex">
      <div className="h-[100dvh]">
        <div className="flex h-[100%] fixed top-0">
          <Sidebar setAuth={setAuth} setUserOrg={setUserOrg} />
        </div>
      </div>
      <div
        className="w-full"
        style={{
          marginLeft: "var(--sidebar-width, 250px)",
          width: "calc(100% - var(--sidebar-width, 250px))",
        }}
      >
        <div className="min-h-screen bg-gray-50 p-6">
          <div className="max-w-7xl mx-auto">
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">Payment Reconcile</h1>
          
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Month</label>
              <select
                value={filters.month}
                onChange={(e) => handleFilterChange('month', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: 12 }, (_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {new Date(0, i).toLocaleString('default', { month: 'long' })}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Year</label>
              <select
                value={filters.year}
                onChange={(e) => handleFilterChange('year', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {Array.from({ length: 5 }, (_, i) => {
                  const year = new Date().getFullYear() - i;
                  return (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  );
                })}
              </select>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Email Filter</label>
              <input
                type="email"
                value={filters.email}
                onChange={(e) => handleFilterChange('email', e.target.value)}
                placeholder="Filter by customer email..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Limit</label>
              <select
                value={filters.limit}
                onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            
            <div className="flex items-end space-x-2">
              <button
                onClick={() => {
                  // Fetch data for all tabs using current filters
                  Promise.all([
                    fetchChargesAndCustomers(false), // For invoices, charges, invoice-payments tabs
                    fetchPaymentAnalysis(false),      // For payment-analysis tab
                    fetchBrexTransactions()          // For brex-transactions tab
                  ]).catch(error => {
                    console.error('Error loading data:', error);
                  });
                }}
                disabled={loading || analysisLoading || brexLoading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              >
                {(loading || analysisLoading || brexLoading) ? 'Loading...' : 'Load Data'}
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="border-b border-gray-200 mb-6">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('invoices')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'invoices'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Invoices
              </button>
              <button
                onClick={() => setActiveTab('charges')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'charges'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Charges
              </button>
              <button
                onClick={() => setActiveTab('invoice-payments')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'invoice-payments'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Invoice Payments
              </button>
              <button
                onClick={() => setActiveTab('payment-analysis')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'payment-analysis'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Payment Analysis
              </button>
              <button
                onClick={() => setActiveTab('brex-transactions')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'brex-transactions'
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Brex Transactions
              </button>
            </nav>
          </div>

          {error && (
            <div className="mb-4 p-4 bg-red-100 border border-red-400 text-red-700 rounded">
              {error}
            </div>
          )}
        </div>

        {/* Brex Transactions Tab */}
        {activeTab === 'brex-transactions' ? (
          <div className="space-y-6">
            {/* Account Info */}
            {brexAccount && (
              <div className="bg-white rounded-lg shadow-sm p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Cash Account Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Account Name</p>
                    <p className="font-semibold">{brexAccount.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Status</p>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                      brexAccount.status === 'ACTIVE' ? 'text-green-600 bg-green-100' : 'text-gray-600 bg-gray-100'
                    }`}>
                      {brexAccount.status}
                    </span>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Current Balance</p>
                    <p className="font-semibold">${(brexAccount.current_balance?.amount / 100).toFixed(2)} {brexAccount.current_balance?.currency}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Transactions Table */}
            <div className="bg-white rounded-lg shadow-sm overflow-hidden">
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex justify-between items-center">
                  <h2 className="text-lg font-semibold text-gray-900">Brex Transactions</h2>
                  <div className="flex space-x-4 text-sm text-gray-600">
                    <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                      Total Transactions: {brexTransactions?.length || 0}
                    </span>
                  </div>
                </div>
              </div>
              
              {brexLoading ? (
                <div className="p-8 text-center">
                  <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <p className="mt-2 text-gray-600">Loading Brex transactions...</p>
                </div>
              ) : (brexTransactions?.length || 0) === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <div className="mb-4">
                    <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Transactions Available</h3>
                  <p className="text-gray-500 mb-4">
                    Click the "Load Data" button above to fetch Brex transactions.
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction ID</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Posted Date</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {Array.isArray(brexTransactions) ? brexTransactions.map((transaction, index) => (
                        <tr key={transaction.id || index} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                            {transaction.id || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                            ${transaction.amount.amount ? (transaction.amount.amount / 100).toFixed(2) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                            {transaction.description || transaction.memo || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {transaction.type || 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {transaction.posted_at_date ? new Date(transaction.posted_at_date).toLocaleDateString() : 'N/A'}
                          </td>
                        </tr>
                      )) : (
                        <tr>
                          <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                            No transactions data available
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        ) : activeTab === 'payment-analysis' ? (
          <div className="space-y-6">
            {/* Summary Cards */}
            {paymentAnalysis && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                        <span className="text-green-600 font-semibold">✓</span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Matched Payments</p>
                      <p className="text-2xl font-semibold text-gray-900">{paymentAnalysis.matchedPayments.length}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center">
                        <span className="text-red-600 font-semibold">!</span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Outstanding</p>
                      <p className="text-2xl font-semibold text-gray-900">{paymentAnalysis.outstandingPayments.length}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-yellow-100 rounded-full flex items-center justify-center">
                        <span className="text-yellow-600 font-semibold">?</span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Draft Invoices</p>
                      <p className="text-2xl font-semibold text-gray-900">{paymentAnalysis.draftInvoices.length}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                        <span className="text-orange-600 font-semibold">🚨</span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Flagged Paid</p>
                      <p className="text-2xl font-semibold text-gray-900">{paymentAnalysis.flaggedPaidInvoices.length}</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg shadow-sm p-6">
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                        <span className="text-gray-600 font-semibold">✗</span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <p className="text-sm font-medium text-gray-500">Void/Uncollectible</p>
                      <p className="text-2xl font-semibold text-gray-900">{paymentAnalysis.voidUncollectible.length}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Analysis Sections */}
            {analysisLoading ? (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading payment analysis...</p>
              </div>
            ) : paymentAnalysis ? (
              <div className="space-y-6">
                {/* Matched Payments */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">✅ Matched Payments</h3>
                    <p className="text-sm text-gray-600">Paid invoices with corresponding succeeded charges</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Charge Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Difference</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice ID</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Charges</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paymentAnalysis.matchedPayments.map((match, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>
                                <div className="font-semibold">{match.customer.name || 'N/A'}</div>
                                <div className="text-xs text-gray-500">{match.customer.email || 'N/A'}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                              {formatAmount(match.totalInvoiceAmount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                              {formatAmount(match.totalChargeAmount)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              <span className={`font-semibold ${match.amountDifference === 0 ? 'text-green-600' : 'text-orange-600'}`}>
                                {match.amountDifference === 0 ? 'Perfect Match' : formatAmount(Math.abs(match.amountDifference))}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                              {match.invoice.id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {match.charge ? '1 charge' : 'No charge'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Outstanding Payments */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">⚠️ Outstanding Payments</h3>
                    <p className="text-sm text-gray-600">Customers with open invoices who should pay</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Due Date</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Days Overdue</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice ID</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paymentAnalysis.outstandingPayments.map((outstanding, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>
                                <div className="font-semibold">{outstanding.customer.name || 'N/A'}</div>
                                <div className="text-xs text-gray-500">{outstanding.customer.email || 'N/A'}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                              {formatAmount(outstanding.invoice.total)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {outstanding.invoice.due_date ? formatDate(outstanding.invoice.due_date) : 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm">
                              {outstanding.daysOverdue !== null ? (
                                <span className={`font-semibold ${outstanding.daysOverdue > 30 ? 'text-red-600' : 'text-orange-600'}`}>
                                  {outstanding.daysOverdue} days
                                </span>
                              ) : (
                                <span className="text-gray-400">N/A</span>
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                              {outstanding.invoice.id}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Draft Invoices */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">📝 Draft Invoices</h3>
                    <p className="text-sm text-gray-600">Customers with draft invoices requiring investigation</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice ID</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paymentAnalysis.draftInvoices.map((draft, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>
                                <div className="font-semibold">{draft.customer.name || 'N/A'}</div>
                                <div className="text-xs text-gray-500">{draft.customer.email || 'N/A'}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                              {formatAmount(draft.invoice.total)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(draft.invoice.created)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                              {draft.invoice.description || 'N/A'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                              {draft.invoice.id}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Flagged Paid Invoices */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">🚨 Flagged Paid Invoices</h3>
                    <p className="text-sm text-gray-600">Paid invoices without corresponding succeeded charges - needs investigation</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice ID</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paymentAnalysis.flaggedPaidInvoices.map((flagged, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>
                                <div className="font-semibold">{flagged.customer.name || 'N/A'}</div>
                                <div className="text-xs text-gray-500">{flagged.customer.email || 'N/A'}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                              {formatAmount(flagged.invoice.total)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full text-green-600 bg-green-100">
                                {flagged.invoice.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(flagged.invoice.created)}
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-900 max-w-xs">
                              <span className="text-orange-600 font-medium">{flagged.reason}</span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                              {flagged.invoice.id}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Void/Uncollectible */}
                <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                  <div className="px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-semibold text-gray-900">❌ Void/Uncollectible Invoices</h3>
                    <p className="text-sm text-gray-600">Invoices marked as void or uncollectible by customer</p>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice ID</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-200">
                        {paymentAnalysis.voidUncollectible.map((voidInvoice, index) => (
                          <tr key={index} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              <div>
                                <div className="font-semibold">{voidInvoice.customer.name || 'N/A'}</div>
                                <div className="text-xs text-gray-500">{voidInvoice.customer.email || 'N/A'}</div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                              {formatAmount(voidInvoice.invoice.total)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                voidInvoice.invoice.status === 'void' ? 'text-red-600 bg-red-100' :
                                'text-gray-600 bg-gray-100'
                              }`}>
                                {voidInvoice.invoice.status}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                              {formatDate(voidInvoice.invoice.created)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                              {voidInvoice.invoice.id}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
                <div className="mb-4">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">No Payment Analysis Data</h3>
                <p className="text-gray-500">
                  Click the "Load Data" button above to fetch payment analysis for the selected period.
                </p>
              </div>
            )}
          </div>
        ) : (
          /* Data Table */
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <h2 className="text-lg font-semibold text-gray-900">
                {activeTab === 'invoices' ? 'Stripe Invoices' : 
                 activeTab === 'charges' ? 'Stripe Charges' : 
                 'Invoice Payments'}
              </h2>
              <div className="flex space-x-4 text-sm text-gray-600">
                <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded">
                  Total Invoices: {totalInvoices}
                </span>
                <span className="bg-green-100 text-green-800 px-2 py-1 rounded">
                  Total Charges: {totalCharges}
                </span>
                <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                  Total Customers: {totalCustomers}
                </span>
                <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded">
                  Invoice Payments: {totalInvoicePayments}
                </span>
              </div>
            </div>
          </div>
          
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <p className="mt-2 text-gray-600">Loading {activeTab}...</p>
            </div>
          ) : (
            (activeTab === 'invoices' && filteredInvoices.length === 0) ||
            (activeTab === 'charges' && filteredCharges.length === 0) ||
            (activeTab === 'invoice-payments' && invoicePayments.length === 0)
          ) ? (
            <div className="p-8 text-center text-gray-500">
              <div className="mb-4">
                <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Data Available</h3>
              <p className="text-gray-500 mb-4">
                Click the "Load Data" button above to fetch {activeTab.replace('-', ' ')} for the selected period.
              </p>
              {(activeTab === 'invoices' || activeTab === 'charges') && filters.email && (
                <p className="text-sm text-gray-400">
                  Note: Email filter "{filters.email}" is applied to filter results.
                </p>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  {activeTab === 'invoices' ? (
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Invoice ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Due Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Created
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                    </tr>
                  ) : activeTab === 'charges' ? (
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Charge ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Description
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Refunded
                      </th>
                    </tr>
                  ) : (
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Invoice ID
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount Paid
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount Requested
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Payment Intent
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                    </tr>
                  )}
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {activeTab === 'invoices' ? (
                    filteredInvoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                          {invoice.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          <div>
                            <div className="font-semibold">{invoice.customer_name || 'N/A'}</div>
                            <div className="text-xs text-gray-500">{invoice.customer_email || 'N/A'}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                          {formatAmount(invoice.total)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            invoice.status === 'paid' ? 'text-green-600 bg-green-100' :
                            invoice.status === 'open' ? 'text-blue-600 bg-blue-100' :
                            invoice.status === 'draft' ? 'text-yellow-600 bg-yellow-100' :
                            invoice.status === 'void' ? 'text-red-600 bg-red-100' :
                            'text-gray-600 bg-gray-100'
                          }`}>
                            {invoice.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {invoice.due_date ? formatDate(invoice.due_date) : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(invoice.created)}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                          {invoice.description || 'N/A'}
                        </td>
                      </tr>
                    ))
                  ) : activeTab === 'charges' ? (
                    filteredCharges.map((charge) => (
                      <tr key={charge.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                          {charge.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                          {formatAmount(charge.amount)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            charge.refunded ? 'text-red-600 bg-red-100' : getStatusColor(charge.status)
                          }`}>
                            {charge.refunded ? 'refunded' : charge.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {charge.customer_info ? (
                            <div>
                              <div className="font-semibold">{charge.customer_info.name || 'N/A'}</div>
                              <div className="text-xs text-gray-500">{charge.customer_info.email || 'N/A'}</div>
                            </div>
                          ) : (
                            charge.customer || 'N/A'
                          )}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 max-w-xs truncate">
                          {charge.description || 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(charge.created)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {charge.refunded ? (
                            <span className="text-red-600 font-semibold">
                              {formatAmount(charge.amount_refunded || 0)}
                            </span>
                          ) : (
                            <span className="text-gray-400">No</span>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    invoicePayments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                          {payment.id}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                          {payment.invoice}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                          {formatAmount(payment.amount_paid)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatAmount(payment.amount_requested)}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            payment.status === 'paid' ? 'text-green-600 bg-green-100' :
                            payment.status === 'open' ? 'text-blue-600 bg-blue-100' :
                            payment.status === 'canceled' ? 'text-red-600 bg-red-100' :
                            'text-gray-600 bg-gray-100'
                          }`}>
                            {payment.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-gray-900">
                          {payment.payment && payment.payment.payment_intent ? payment.payment.payment_intent : 'N/A'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatDate(payment.created)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RevOp;
